import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Facebook, Instagram, MessageCircle, Loader2, CheckCircle2, AlertCircle, RefreshCw, Trash2, Plug, Copy, ChevronDown, ShieldAlert } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useApp } from '../contexts/AppContext';
import { errorText } from '../lib/errorText';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  connected: { label: 'Conectada', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30' },
  pending_activation: { label: 'Falta activar', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30' },
  pending_registration: { label: 'Falta registrar número', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30' },
  token_invalid: { label: 'Permiso expirado', cls: 'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30' },
  revoked: { label: 'Revocada en Meta', cls: 'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30' },
  expired: { label: 'Token caducado', cls: 'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30' },
  error: { label: 'Error', cls: 'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30' },
};

const copy = (v) => { navigator.clipboard?.writeText(v); toast.success('Copiado'); };

export const MetaConnectionCenter = () => {
  const { campaigns } = useApp();
  const [config, setConfig] = useState(null);
  const [conns, setConns] = useState([]);
  const [busy, setBusy] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const popupRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([axios.get(`${API}/meta/config`), axios.get(`${API}/meta/connections`)]);
      setConfig(c.data); setConns(l.data || []);
    } catch (e) { toast.error(errorText(e, 'No se pudo cargar el estado de Meta')); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onMsg = (ev) => {
      const d = ev.data || {};
      if (d.type !== 'meta-oauth') return;
      setBusy(null);
      if (d.status === 'ok') toast.success(d.detail || 'Cuenta de Meta conectada');
      else if (d.status === 'cancelled') toast.warning(d.detail || 'Autorización cancelada');
      else toast.error(d.detail || 'Meta rechazó la conexión');
      load();
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [load]);

  const connect = async (channel) => {
    if (!config?.configured) {
      toast.error('Faltan credenciales de Meta en el servidor', { description: config?.blockers?.map(b => b.fix).join(' ') });
      setShowSetup(true); return;
    }
    setBusy(channel);
    try {
      const { data } = await axios.post(`${API}/meta/connect/start`, { channel });
      const w = window.open(data.url, 'deal-meta-oauth', 'width=620,height=760,menubar=no,toolbar=no');
      if (!w) { setBusy(null); toast.error('El navegador bloqueó la ventana emergente. Permite pop-ups para este sitio.'); return; }
      popupRef.current = w;
      const t = setInterval(() => { if (w.closed) { clearInterval(t); setBusy(null); load(); } }, 700);
    } catch (e) { setBusy(null); toast.error(errorText(e, 'No se pudo iniciar la autorización')); }
  };

  const act = async (id, action) => {
    setBusy(id + action);
    try {
      if (action === 'activate') { await axios.post(`${API}/meta/connections/${id}/activate`); toast.success('Página activada: Meta ya entrega los mensajes a DEAL'); }
      if (action === 'test') { const { data } = await axios.post(`${API}/meta/connections/${id}/test`); data.ok ? toast.success(`Conexión válida · ${data.page}${data.instagram ? ` · @${data.instagram}` : ''}${data.subscribed ? '' : ' · falta activar'}`) : toast.error(data.error); }
      if (action === 'delete') { await axios.delete(`${API}/meta/connections/${id}`); toast.success('Página desconectada'); }
      await load();
    } catch (e) { toast.error(errorText(e, 'Meta devolvió un error')); } finally { setBusy(null); }
  };

  const assign = async (id, field, value) => {
    try { await axios.put(`${API}/meta/connections/${id}`, { [field]: value === 'none' ? null : value }); await load(); toast.success('Campaña asignada'); }
    catch (e) { toast.error(errorText(e, 'No se pudo asignar')); }
  };

  const pages = conns.filter(c => c.channel === 'page');
  const wa = conns.filter(c => c.channel === 'whatsapp');
  const anyConnected = conns.some(c => c.status === 'connected');

  return (
    <Card className="glass-card" data-testid="meta-connection-center">
      <CardContent className="p-4 sm:p-5 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-[#1877F2]/15 flex items-center justify-center shrink-0"><Facebook className="w-5 h-5 text-[#1877F2]" /></div>
            <div className="min-w-0">
              <p className="font-semibold">Meta · Messenger, Instagram y WhatsApp</p>
              <p className="text-xs text-muted-foreground">Cada empresa autoriza sus propias páginas y números con su cuenta de Meta. DEAL nunca te pide tokens.</p>
            </div>
          </div>
          <Badge variant="outline" className={cn('rounded-full', anyConnected ? STATUS.connected.cls : 'bg-zinc-500/10 text-zinc-400 border-zinc-500/30')} data-testid="meta-status-badge">
            {anyConnected ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <AlertCircle className="w-3 h-3 mr-1" />}{anyConnected ? 'Conectado' : 'Sin conexión'}
          </Badge>
        </div>

        {config && !config.configured && (
          <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-3 text-xs space-y-1" data-testid="meta-blockers">
            <p className="font-semibold text-red-700 dark:text-red-400 flex items-center gap-1"><ShieldAlert className="w-3.5 h-3.5" /> Faltan credenciales en el servidor</p>
            {config.blockers.map(b => <p key={b.key}><b>{b.key}</b>: {b.fix}</p>)}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
          <Button onClick={() => connect('pages')} disabled={!!busy} className="bg-[#1877F2] hover:bg-[#1465d6] text-white w-full sm:w-auto" data-testid="meta-connect-pages-btn">
            {busy === 'pages' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Facebook className="w-4 h-4 mr-2" />}Conectar páginas de Facebook
          </Button>
          <Button onClick={() => connect('instagram')} disabled={!!busy} variant="outline" className="w-full sm:w-auto border-pink-500/40 text-pink-600 dark:text-pink-400" data-testid="meta-connect-instagram-btn">
            {busy === 'instagram' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Instagram className="w-4 h-4 mr-2" />}Conectar Instagram
          </Button>
          <WhatsAppSignupButton config={config} campaigns={campaigns} onDone={load} />
        </div>
        <p className="text-[11px] text-muted-foreground">
          Instagram se conecta a través de la página de Facebook vinculada a la cuenta profesional. Mientras la verificación de empresa de OVC
          siga en revisión en Meta, solo pueden conectarse cuentas con rol en la app (administradores o testers); las empresas externas podrán hacerlo cuando Meta apruebe los permisos.
        </p>

        {pages.length > 0 && (
          <div className="space-y-2" data-testid="meta-pages-list">
            {pages.map(c => <PageRow key={c.id} c={c} campaigns={campaigns} busy={busy} act={act} assign={assign} />)}
          </div>
        )}
        {wa.length > 0 && (
          <div className="space-y-2" data-testid="meta-wa-list">
            {wa.map(c => <WhatsAppRow key={c.id} c={c} campaigns={campaigns} config={config} busy={busy} setBusy={setBusy} load={load} />)}
          </div>
        )}

        <button onClick={() => setShowSetup(s => !s)} className="text-xs text-muted-foreground flex items-center gap-1 hover:text-foreground" data-testid="meta-setup-toggle">
          <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', showSetup && 'rotate-180')} /> Datos para registrar en Meta for Developers
        </button>
        {showSetup && config && (
          <div className="rounded-2xl bg-muted/40 p-3 text-xs space-y-1.5 font-mono" data-testid="meta-setup-panel">
            {[['Redirect URI (OAuth)', config.redirect_uri], ['Webhook callback', config.webhook_url], ['Verify token', config.verify_token], ['Deauthorize callback', config.deauthorize_url], ['Data deletion callback', config.data_deletion_url]].filter(([, v]) => v).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 min-w-0"><span className="text-muted-foreground w-40 shrink-0 font-sans">{k}</span><span className="truncate flex-1">{v}</span><button onClick={() => copy(v)} className="shrink-0"><Copy className="w-3.5 h-3.5" /></button></div>
            ))}
            <p className="font-sans text-muted-foreground pt-1">Permisos: {config.scopes.pages.join(', ')} · WhatsApp: {config.scopes.whatsapp.join(', ')} · Graph {config.graph_version}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const PageRow = ({ c, campaigns, busy, act, assign }) => {
  const st = STATUS[c.status] || STATUS.error;
  return (
    <div className="rounded-2xl border border-border p-3 space-y-2" data-testid={`meta-page-${c.id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <Facebook className="w-4 h-4 text-[#1877F2] shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{c.name}</p>
          <p className="text-[11px] text-muted-foreground truncate">Página {c.page_id}{c.ig_username ? ` · Instagram @${c.ig_username}` : ' · sin Instagram vinculado'}</p>
        </div>
        <Badge variant="outline" className={cn('rounded-full text-[10px]', st.cls)} data-testid={`meta-page-status-${c.id}`}>{st.label}</Badge>
      </div>
      {c.error && <p className="text-[11px] text-red-600 dark:text-red-400">{c.error}</p>}
      <ExpiryHint c={c} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <p className="text-[10px] text-muted-foreground mb-0.5">Messenger → campaña</p>
          <Select value={c.campaign_id || 'none'} onValueChange={(v) => assign(c.id, 'campaign_id', v)}>
            <SelectTrigger className="h-8 text-xs" data-testid={`meta-page-campaign-${c.id}`}><SelectValue placeholder="Sin asignar" /></SelectTrigger>
            <SelectContent><SelectItem value="none">Bandeja sin clasificar</SelectItem>{campaigns.map(k => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {c.ig_id && (
          <div>
            <p className="text-[10px] text-muted-foreground mb-0.5">Instagram → campaña</p>
            <Select value={c.ig_campaign_id || 'none'} onValueChange={(v) => assign(c.id, 'ig_campaign_id', v)}>
              <SelectTrigger className="h-8 text-xs" data-testid={`meta-ig-campaign-${c.id}`}><SelectValue placeholder="Sin asignar" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Bandeja sin clasificar</SelectItem>{campaigns.map(k => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {!c.subscribed && c.status !== 'revoked' && (
          <Button size="sm" onClick={() => act(c.id, 'activate')} disabled={!!busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`meta-page-activate-${c.id}`}>
            {busy === c.id + 'activate' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plug className="w-3.5 h-3.5 mr-1" />}Activar recepción
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={() => act(c.id, 'test')} disabled={!!busy} data-testid={`meta-page-test-${c.id}`}>
          {busy === c.id + 'test' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}Probar
        </Button>
        <Button size="sm" variant="ghost" onClick={() => act(c.id, 'delete')} disabled={!!busy} className="text-red-600 dark:text-red-400" data-testid={`meta-page-delete-${c.id}`}>
          <Trash2 className="w-3.5 h-3.5 mr-1" />Desconectar
        </Button>
      </div>
    </div>
  );
};

const ExpiryHint = ({ c }) => {
  if (c.expires_in_days == null) return null;
  const warn = c.expires_in_days <= 7;
  return (
    <p className={cn('text-[11px]', warn ? 'text-amber-700 dark:text-amber-400 font-medium' : 'text-muted-foreground')} data-testid={`meta-expiry-${c.id}`}>
      {c.expires_in_days < 0 ? 'El permiso caducó.' : `El permiso vence en ${c.expires_in_days} día(s)`}{warn ? ' · reconecta para no perder mensajes' : ''}
    </p>
  );
};

const WhatsAppRow = ({ c, campaigns, config, busy, setBusy, load }) => {
  const [pin, setPin] = useState('');
  const st = STATUS[c.status] || STATUS.error;
  const call = async (fn, okMsg) => {
    setBusy(c.id);
    try { const r = await fn(); if (okMsg) toast.success(typeof okMsg === 'function' ? okMsg(r?.data) : okMsg); await load(); }
    catch (e) { toast.error(errorText(e, 'Meta devolvió un error')); } finally { setBusy(null); }
  };
  return (
    <div className="rounded-2xl border border-border p-3 space-y-2 text-sm" data-testid={`meta-wa-${c.id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <MessageCircle className="w-4 h-4 text-emerald-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{c.name || c.display_phone}</p>
          <p className="text-[11px] text-muted-foreground truncate">{c.display_phone} · campaña {campaigns.find(x => x.id === c.campaign_id)?.name || '—'}</p>
        </div>
        <Badge variant="outline" className={cn('rounded-full text-[10px]', st.cls)} data-testid={`meta-wa-status-${c.id}`}>{st.label}</Badge>
      </div>
      {c.error && <p className="text-[11px] text-red-600 dark:text-red-400">{c.error}</p>}
      <ExpiryHint c={c} />
      {c.status === 'pending_registration' && (
        <div className="flex gap-2">
          <Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="PIN de 6 dígitos" className="h-8 text-xs max-w-[180px]" data-testid={`meta-wa-pin-${c.id}`} />
          <Button size="sm" disabled={busy || pin.length !== 6} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`meta-wa-register-${c.id}`}
            onClick={() => call(() => axios.post(`${API}/meta/whatsapp/${c.id}/register`, { pin }), 'Número registrado: WhatsApp listo para enviar y recibir')}>
            Registrar número
          </Button>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" disabled={!!busy} data-testid={`meta-wa-test-${c.id}`}
          onClick={() => call(() => axios.post(`${API}/meta/whatsapp/${c.id}/test`), (d) => d?.ok ? `Conexión válida · ${d.phone} · calidad ${d.quality || '—'}${d.subscribed ? '' : ' · webhook no suscrito'}` : `Fallo: ${d?.error}`)}>
          {busy === c.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}Probar
        </Button>
        {(c.needs_reconnect || c.status !== 'connected') && <WhatsAppSignupButton config={config} campaigns={campaigns} onDone={load} presetCampaign={c.campaign_id} label="Reconectar" />}
        <Button size="sm" variant="ghost" disabled={!!busy} className="text-red-600 dark:text-red-400" data-testid={`meta-wa-delete-${c.id}`}
          onClick={() => call(() => axios.delete(`${API}/meta/connections/${c.id}`), 'WhatsApp desconectado')}>
          <Trash2 className="w-3.5 h-3.5 mr-1" />Desconectar
        </Button>
      </div>
    </div>
  );
};

const loadFbSdk = (appId, version) => new Promise((resolve) => {
  if (window.FB) return resolve(window.FB);
  window.fbAsyncInit = () => { window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version }); resolve(window.FB); };
  const s = document.createElement('script');
  s.src = 'https://connect.facebook.net/es_LA/sdk.js'; s.async = true; s.defer = true; s.crossOrigin = 'anonymous';
  document.body.appendChild(s);
});

const WhatsAppSignupButton = ({ config, campaigns, onDone, presetCampaign = '', label = 'Conectar WhatsApp' }) => {
  const [open, setOpen] = useState(false);
  const [campaignId, setCampaignId] = useState(presetCampaign);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef({});
  const ready = config?.whatsapp?.embedded_signup_ready;
  const [sdkReady, setSdkReady] = useState(!!window.FB);

  useEffect(() => {
    if (ready && config?.app_id) loadFbSdk(config.app_id, config.graph_version).then(() => setSdkReady(true)).catch(() => setSdkReady(false));
  }, [ready, config?.app_id, config?.graph_version]);

  useEffect(() => {
    const onMsg = (ev) => {
      if (!String(ev.origin).endsWith('facebook.com')) return;
      try {
        const d = typeof ev.data === 'string' ? JSON.parse(ev.data) : ev.data;
        if (d?.type === 'WA_EMBEDDED_SIGNUP' && d.event === 'FINISH') sessionRef.current = { waba_id: d.data?.waba_id, phone_number_id: d.data?.phone_number_id };
        if (d?.type === 'WA_EMBEDDED_SIGNUP' && d.event === 'CANCEL') toast.warning('Registro de WhatsApp cancelado');
      } catch { /* ignore */ }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const launch = () => {
    if (!ready) {
      const b = config?.whatsapp?.blocker;
      toast.error('WhatsApp (Embedded Signup) aún no está habilitado', { description: b ? `${b.key}: ${b.fix}` : 'Faltan credenciales de Meta.', duration: 12000 });
      return;
    }
    if (!campaignId) return toast.warning('Elige la campaña que atenderá este número');
    if (!window.FB) { toast.error('El SDK de Meta no cargó. Revisa que este dominio esté en "Dominios permitidos para el SDK de JavaScript" de la app y recarga.'); return; }
    setBusy(true);
    sessionRef.current = {};
    // FB.login must run synchronously inside the click to avoid the popup blocker
    const onLogin = async (resp) => {
      const code = resp?.authResponse?.code;
      if (!code) { setBusy(false); toast.warning(resp?.status === 'unknown' ? 'Cancelaste el registro de WhatsApp' : 'No se completó la autorización de WhatsApp'); return; }
      const { waba_id, phone_number_id } = sessionRef.current;
      if (!waba_id || !phone_number_id) { setBusy(false); toast.error('Meta no devolvió el número seleccionado. Repite el registro y termina todos los pasos.'); return; }
      try {
        const { data } = await axios.post(`${API}/meta/whatsapp/exchange`, { code, waba_id, phone_number_id, campaign_id: campaignId, pin: pin || null });
        toast.success(data.registered ? `WhatsApp ${data.display_phone} conectado y verificado` : `Número ${data.display_phone} vinculado · falta registrar con el PIN para activarlo`);
        setOpen(false); onDone?.();
      } catch (e) { toast.error(errorText(e, 'No se pudo completar la conexión de WhatsApp')); } finally { setBusy(false); }
    };
    window.FB.login(function (resp) { onLogin(resp); }, { config_id: config.whatsapp.config_id, response_type: 'code', override_default_response_type: true, extras: { setup: {}, featureType: '', sessionInfoVersion: '3' } });
  };

  return (
    <>
      <Button onClick={() => (ready ? setOpen(o => !o) : launch())} variant="outline" size={label === 'Reconectar' ? 'sm' : 'default'} className={cn('w-full sm:w-auto border-emerald-500/40 text-emerald-700 dark:text-emerald-400', !ready && 'border-dashed')} data-testid={label === 'Reconectar' ? 'meta-wa-reconnect-btn' : 'meta-connect-whatsapp-btn'}>
        <MessageCircle className="w-4 h-4 mr-2" />{label}{!ready && <span className="ml-2 text-[10px] rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 px-1.5">pendiente Meta</span>}
      </Button>
      {open && ready && (
        <div className="basis-full rounded-2xl border border-emerald-500/30 p-3 space-y-2 text-sm" data-testid="meta-wa-signup-panel">
          <p className="text-xs text-muted-foreground">Elige la campaña y, si ya tienes el PIN de verificación en dos pasos del número, escríbelo para registrarlo de una vez.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Select value={campaignId} onValueChange={setCampaignId}>
              <SelectTrigger className="h-9" data-testid="meta-wa-campaign"><SelectValue placeholder="Campaña" /></SelectTrigger>
              <SelectContent>{campaigns.map(k => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="PIN 6 dígitos (opcional)" className="h-9" data-testid="meta-wa-pin" />
            <Button onClick={launch} disabled={busy || !sdkReady} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9" data-testid="meta-wa-launch">
              {busy || !sdkReady ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Abrir registro de Meta'}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
